// Activos de mantenimiento — tabla con filtros (planta, búsqueda) y
// crear/editar con selects encadenados planta → línea (la línea se filtra por
// la planta elegida, así el 400 linea_invalida no puede dispararse desde UI).

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { ActivoMtto, ActivoMttoUpdate, TipoActivoMtto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { useEstructuraMtto } from './estructura';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import { useActivosMtto, useActualizarActivoMtto, useCrearActivoMtto } from './queries';
import {
  BannerSinRolMtto,
  TIPOS_ACTIVO,
  TIPO_ACTIVO_LABEL,
  esCodigoApi,
  mensajeError,
  usePermisosMtto,
} from './shared';

export function ActivosMttoPage() {
  const { puedeOperar } = usePermisosMtto();
  const [plantaFiltro, setPlantaFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [dialogo, setDialogo] = useState<
    { modo: 'crear' } | { modo: 'editar'; activo: ActivoMtto } | null
  >(null);

  const estructura = useEstructuraMtto();
  const { data, isLoading, isError, error } = useActivosMtto({
    ...(plantaFiltro ? { planta_id: plantaFiltro } : {}),
    ...(busqueda.trim() ? { q: busqueda.trim() } : {}),
  });

  return (
    <MantenimientoShell
      titulo="Activos"
      subtitulo={data ? `${data.length} activo(s) con los filtros actuales` : undefined}
      acciones={
        puedeOperar ? (
          <Button onClick={() => setDialogo({ modo: 'crear' })}>Crear activo</Button>
        ) : undefined
      }
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 pt-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="act-planta">Planta</Label>
            <Select
              id="act-planta"
              value={plantaFiltro}
              onChange={(e) => setPlantaFiltro(e.target.value)}
              disabled={!estructura.disponible}
            >
              <option value="">Todas</option>
              {estructura.plantas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="act-q">Buscar</Label>
            <Input
              id="act-q"
              placeholder="Nombre o código…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? <CargandoModulo mensaje="Cargando activos…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}

      {data && data.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Sin activos con esos filtros.
          </CardContent>
        </Card>
      ) : null}

      {data && data.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>Código</TH>
              <TH>Nombre</TH>
              <TH align="center">Tipo</TH>
              <TH>Planta</TH>
              <TH>Línea</TH>
              <TH align="center">Estado</TH>
              {puedeOperar ? <TH align="right" /> : null}
            </TR>
          </THead>
          <TBody>
            {data.map((a) => (
              <TR key={a.id}>
                <TD className="font-mono text-xs text-fg-secondary">{a.codigo ?? '—'}</TD>
                <TD className="text-sm text-fg">
                  {a.nombre}
                  {a.ubicacion ? (
                    <div className="text-[11px] text-fg-tertiary">{a.ubicacion}</div>
                  ) : null}
                </TD>
                <TD align="center" className="text-xs text-fg-secondary">
                  {TIPO_ACTIVO_LABEL[a.tipo]}
                </TD>
                <TD className="text-xs text-fg-secondary">{a.planta_nombre ?? '—'}</TD>
                <TD className="text-xs text-fg-secondary">{a.linea_nombre ?? '—'}</TD>
                <TD align="center">
                  <Badge tone={a.estado === 'valido' ? 'ok' : 'neutral'} dot>
                    {a.estado === 'valido' ? 'Activo' : 'Invalidado'}
                  </Badge>
                </TD>
                {puedeOperar ? (
                  <TD align="right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDialogo({ modo: 'editar', activo: a })}
                    >
                      Editar
                    </Button>
                  </TD>
                ) : null}
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}

      {dialogo ? (
        <ActivoFormDialog
          activo={dialogo.modo === 'editar' ? dialogo.activo : null}
          onClose={() => setDialogo(null)}
        />
      ) : null}
    </MantenimientoShell>
  );
}

// =============================================================================
// Crear / editar activo — selects encadenados planta → línea.
// =============================================================================

function ActivoFormDialog({ activo, onClose }: { activo: ActivoMtto | null; onClose: () => void }) {
  const crear = useCrearActivoMtto();
  const actualizar = useActualizarActivoMtto();
  const estructura = useEstructuraMtto();
  const [sinRol, setSinRol] = useState(false);

  const [nombre, setNombre] = useState(activo?.nombre ?? '');
  const [codigo, setCodigo] = useState(activo?.codigo ?? '');
  const [tipo, setTipo] = useState<TipoActivoMtto>(activo?.tipo ?? 'equipo');
  const [ubicacion, setUbicacion] = useState(activo?.ubicacion ?? '');
  const [plantaId, setPlantaId] = useState(activo?.planta_id ?? '');
  const [lineaId, setLineaId] = useState(activo?.linea_id ?? '');

  const lineasDePlanta = useMemo(
    () => estructura.lineas.filter((l) => l.planta_id === plantaId),
    [estructura.lineas, plantaId],
  );

  const onCambioPlanta = (nueva: string) => {
    setPlantaId(nueva);
    // La línea depende de la planta: al cambiarla se limpia la selección.
    setLineaId('');
  };

  const guardando = crear.isPending || actualizar.isPending;

  const onGuardar = async () => {
    if (!nombre.trim()) {
      toast.error('Escriba el nombre del activo');
      return;
    }
    if (!plantaId) {
      toast.error('Seleccione la planta del activo');
      return;
    }
    try {
      if (activo) {
        const patch: ActivoMttoUpdate = {
          nombre: nombre.trim(),
          tipo,
          ...(codigo.trim() ? { codigo: codigo.trim() } : {}),
          ...(ubicacion.trim() ? { ubicacion: ubicacion.trim() } : {}),
          planta_id: plantaId,
          // null explícito desvincula la línea (activo a nivel planta).
          linea_id: lineaId === '' ? null : lineaId,
        };
        await actualizar.mutateAsync({ id: activo.id, patch });
        toast.success('Activo actualizado');
      } else {
        await crear.mutateAsync({
          nombre: nombre.trim(),
          tipo,
          planta_id: plantaId,
          ...(codigo.trim() ? { codigo: codigo.trim() } : {}),
          ...(ubicacion.trim() ? { ubicacion: ubicacion.trim() } : {}),
          ...(lineaId ? { linea_id: lineaId } : {}),
        });
        toast.success('Activo creado');
      }
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'rol_mantenimiento_no_autorizado')) {
        setSinRol(true);
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{activo ? 'Editar activo' : 'Crear activo'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {sinRol ? <BannerSinRolMtto accion="gestionar activos" /> : null}
          {!estructura.disponible && !estructura.cargando ? (
            <p className="rounded border border-border bg-surface-overlay px-3 py-2 text-xs text-fg-secondary">
              Su rol no tiene acceso al catálogo de plantas y líneas; contacte al administrador para
              crear activos.
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-nombre">Nombre *</Label>
              <Input
                id="act-form-nombre"
                value={nombre}
                maxLength={120}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-codigo">Código</Label>
              <Input
                id="act-form-codigo"
                value={codigo}
                maxLength={40}
                onChange={(e) => setCodigo(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-tipo">Tipo *</Label>
              <Select
                id="act-form-tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoActivoMtto)}
              >
                {TIPOS_ACTIVO.map((t) => (
                  <option key={t} value={t}>
                    {TIPO_ACTIVO_LABEL[t]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-ubicacion">Ubicación</Label>
              <Input
                id="act-form-ubicacion"
                value={ubicacion}
                maxLength={200}
                placeholder="Ej. Bodega 2, pasillo A"
                onChange={(e) => setUbicacion(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-planta">Planta *</Label>
              <Select
                id="act-form-planta"
                value={plantaId}
                disabled={!estructura.disponible}
                onChange={(e) => onCambioPlanta(e.target.value)}
              >
                <option value="">Seleccione…</option>
                {estructura.plantas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="act-form-linea">Línea</Label>
              <Select
                id="act-form-linea"
                value={lineaId}
                disabled={!plantaId || lineasDePlanta.length === 0}
                onChange={(e) => setLineaId(e.target.value)}
              >
                <option value="">Sin línea (nivel planta)</option>
                {lineasDePlanta.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nombre}
                  </option>
                ))}
              </Select>
              {plantaId && lineasDePlanta.length === 0 ? (
                <p className="text-[11px] text-fg-tertiary">
                  La planta seleccionada no tiene líneas registradas.
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={guardando || !estructura.disponible}
            onClick={() => {
              void onGuardar();
            }}
          >
            {guardando ? 'Guardando…' : activo ? 'Guardar cambios' : 'Crear activo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
